/**
 * 图片导出服务
 * 支持 PNG, JPEG, SVG 格式的图片导出
 */

import html2canvas from 'html2canvas'
import { ExportOptions, ExportServiceConfig } from '../../../types/export'
import { downloadBlob } from '../../../utils/download'
import {
  EXPORT_STYLES,
  CONTAINER_STYLES,
  parseMarkdown,
  waitForRender,
  generateExportFilename
} from './exportStyles'

/**
 * 创建用于渲染的容器元素
 */
const createRenderContainer = (content: string, title?: string): HTMLElement => {
  const htmlContent = parseMarkdown(content)

  // 创建容器
  const container = document.createElement('div')
  container.style.fontFamily = CONTAINER_STYLES.fontFamily
  container.style.lineHeight = CONTAINER_STYLES.lineHeight
  container.style.color = CONTAINER_STYLES.color
  container.style.backgroundColor = CONTAINER_STYLES.backgroundColor
  container.style.maxWidth = '800px'
  container.style.margin = '0 auto'
  container.style.padding = '40px'
  container.style.width = '800px'
  container.style.boxSizing = 'border-box'

  // 创建样式元素
  const style = document.createElement('style')
  style.textContent = EXPORT_STYLES
  container.appendChild(style)

  // 添加标题
  if (title) {
    const titleEl = document.createElement('h1')
    titleEl.textContent = title
    titleEl.style.textAlign = 'center'
    titleEl.style.marginBottom = '30px'
    titleEl.style.color = '#333'
    container.appendChild(titleEl)
  }

  // 添加内容
  const contentDiv = document.createElement('div')
  contentDiv.innerHTML = htmlContent
  container.appendChild(contentDiv)

  return container
}

/**
 * 默认图片导出配置
 */
const defaultImageConfig: ExportServiceConfig['image'] = {
  quality: 0.9,
  scale: 2,
  format: 'png'
}

/**
 * 导出为 PNG 或 JPEG
 */
export const exportToImage = async (
  content: string,
  options: ExportOptions,
  format: 'png' | 'jpeg' = 'png',
  element?: HTMLElement | null
): Promise<void> => {
  let container: HTMLElement | null = null

  try {
    let targetDom: HTMLElement

    if (element) {
      targetDom = element
    } else {
      container = createRenderContainer(content, options.title)

      container.style.position = 'fixed'
      container.style.top = '0'
      container.style.left = '0'
      container.style.visibility = 'hidden'
      container.style.zIndex = '-9999'
      container.style.pointerEvents = 'none'
      document.body.appendChild(container)

      await waitForRender(container)
      targetDom = container
    }

    console.log('导出图片:', { format, width: targetDom.offsetWidth, height: targetDom.offsetHeight })

    const canvas = await html2canvas(targetDom, {
      scale: window.devicePixelRatio,
      useCORS: true,
      logging: false,
      allowTaint: true,
      backgroundColor: '#ffffff'
    })

    // 转换为指定格式
    const mimeType = format === 'png' ? 'image/png' : 'image/jpeg'
    const quality = format === 'jpeg' ? defaultImageConfig.quality : undefined

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('无法生成 blob'))
          }
        },
        mimeType,
        quality
      )
    })

    // 下载文件
    const filename = generateExportFilename(format, options.filename)
    downloadBlob(blob, filename)

  } catch (error) {
    console.error('图片导出失败:', error)
    throw new Error(`图片导出失败: ${error instanceof Error ? error.message : '未知错误'}`)
  } finally {
    // 清理容器
    if (container && container.parentElement) {
      document.body.removeChild(container)
    }
  }
}

/**
 * 导出为 SVG
 */
export const exportToSVG = async (
  content: string,
  options: ExportOptions,
  element?: HTMLElement | null
): Promise<void> => {
  let container: HTMLElement | null = null

  try {
    let targetDom: HTMLElement

    if (element) {
      targetDom = element
    } else {
      container = createRenderContainer(content, options.title)

      container.style.position = 'fixed'
      container.style.top = '0'
      container.style.left = '0'
      container.style.visibility = 'hidden'
      container.style.zIndex = '-9999'
      container.style.pointerEvents = 'none'
      document.body.appendChild(container)

      await waitForRender(container)
      targetDom = container
    }

    const canvas = await html2canvas(targetDom, {
      scale: window.devicePixelRatio,
      useCORS: true,
      logging: false,
      allowTaint: true,
      backgroundColor: '#ffffff'
    })

    // 获取PNG数据
    const pngData = canvas.toDataURL('image/png', defaultImageConfig.quality)
    
    // 创建包含PNG图片的SVG
    const svgData = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" 
     xmlns:xlink="http://www.w3.org/1999/xlink" 
     width="${canvas.width}" 
     height="${canvas.height}"
     viewBox="0 0 ${canvas.width} ${canvas.height}">
  <title>${options.title || 'Markdown Document'}</title>
  <desc>Exported from MarkFlow Markdown Editor</desc>
  <image x="0" y="0" width="${canvas.width}" height="${canvas.height}" 
         xlink:href="${pngData}" />
</svg>`
    
    // 创建 SVG blob
    const blob = new Blob([svgData], { type: 'image/svg+xml' })

    // 下载文件
    const filename = generateExportFilename('svg', options.filename)
    downloadBlob(blob, filename)

  } catch (error) {
    console.error('SVG 导出失败:', error)
    throw new Error(`SVG 导出失败: ${error instanceof Error ? error.message : '未知错误'}`)
  } finally {
    // 清理容器
    if (container && container.parentElement) {
      document.body.removeChild(container)
    }
  }
}

/**
 * 预览图片（返回 blob URL）
 */
export const previewImage = async (
  content: string,
  options: ExportOptions,
  format: 'png' | 'jpeg' = 'png'
): Promise<string> => {
  let container: HTMLElement | null = null

  try {
    // 创建渲染容器
    container = createRenderContainer(content, options.title)

    // 将容器添加到 body
    container.style.position = 'fixed'
    container.style.top = '0'
    container.style.left = '0'
    container.style.visibility = 'hidden'
    container.style.zIndex = '-9999'
    container.style.pointerEvents = 'none'
    document.body.appendChild(container)

    // 等待渲染完成（使用智能等待而非固定延时）
    await waitForRender(container)

    // 配置 html2canvas 选项
    // 关键：使用 onclone 回调使克隆的元素可见
    const canvasOptions = {
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: container.offsetWidth,
      height: container.offsetHeight,
      onclone: (_clonedDoc: Document, clonedElement: HTMLElement) => {
        // 使克隆的元素可见，html2canvas 无法捕获 visibility: hidden 的元素
        clonedElement.style.visibility = 'visible'
        clonedElement.style.position = 'static'
        clonedElement.style.top = 'auto'
        clonedElement.style.left = 'auto'
      }
    }

    // 生成 canvas
    const canvas = await html2canvas(container, canvasOptions)

    const mimeType = format === 'png' ? 'image/png' : 'image/jpeg'
    const quality = format === 'jpeg' ? defaultImageConfig.quality : undefined

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(URL.createObjectURL(blob))
          } else {
            reject(new Error('无法生成图片 blob'))
          }
        },
        mimeType,
        quality
      )
    })

  } catch (error) {
    console.error('图片预览失败:', error)
    throw new Error(`图片预览失败: ${error instanceof Error ? error.message : '未知错误'}`)
  } finally {
    // 清理容器
    if (container && container.parentElement) {
      document.body.removeChild(container)
    }
  }
}

/**
 * 检查图片导出支持
 */
export const checkImageSupport = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof document !== 'undefined' && 
         typeof Blob !== 'undefined' &&
         typeof HTMLCanvasElement !== 'undefined'
}